import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config/api";
import {
  TrendingUp, TrendingDown, ShieldCheck, Target, AlertCircle, BarChart3,
  Sparkles, MinusCircle, ChevronDown, ChevronRight, Crosshair,
} from "lucide-react";
import {
  BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";

/**
 * "Meaningful Edge" — selective high-confidence next-day signal based on
 * documented technical setups (RSI extremes, VIX spikes, gap reversals, etc.).
 * Only fires when ≥ N rules agree on direction.
 */
export default function MeaningfulEdge({ ticker }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [years, setYears] = useState(10);
  const [minAgree, setMinAgree] = useState(3);
  const [showRules, setShowRules] = useState(false);

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await axios.get(
          `${API}/stock/meaningful-edge/${ticker}?years=${years}&min_agree=${minAgree}`,
          { timeout: 90000 }
        );
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Failed to compute edge");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ticker, years, minAgree]);

  if (loading) {
    return (
      <div className="rounded border border-gray-800 bg-gray-900/60 p-6 text-center">
        <ShieldCheck className="w-8 h-8 text-amber-400 mx-auto animate-pulse" />
        <div className="mt-2 text-sm text-gray-300">Backtesting {years}y of high-confidence rules for {ticker}…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-3 space-y-2" data-testid="meaningful-edge-skeleton">
        <div className="flex items-center gap-2 mb-1">
          <span className="inline-block w-2 h-2 rounded-full bg-amber-400/60 animate-pulse" />
          <span className="text-[11px] text-amber-300/80">Refreshing meaningful edge…</span>
        </div>
        <div className="h-28 bg-slate-800/40 rounded animate-pulse" />
      </div>
    );
  }
  if (!data) return null;

  const bt = data.backtest;
  const ts = data.today_signal;
  const acc = bt?.accuracy;

  const verdictColor = acc >= 0.75 ? "text-emerald-300 border-emerald-700/60 bg-emerald-950/30"
    : acc >= 0.65 ? "text-green-300 border-green-700/60 bg-green-950/30"
    : acc >= 0.58 ? "text-blue-300 border-blue-700/60 bg-blue-950/30"
    : "text-amber-300 border-amber-700/60 bg-amber-950/30";

  return (
    <div className="space-y-3" data-testid="meaningful-edge-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2 bg-[#0d0d0d] border border-gray-800 rounded p-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-amber-400" />
          <h3 className="text-base font-semibold text-white">{ticker} · Meaningful Edge</h3>
          <span className="text-[10px] text-gray-500 font-mono">As of {data.as_of} · ${data.last_close}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={years}
            onChange={(e) => setYears(Number(e.target.value))}
            data-testid="medge-years-select"
            className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-xs"
          >
            <option value={3}>3y backtest</option>
            <option value={5}>5y backtest</option>
            <option value={10}>10y backtest</option>
            <option value={15}>15y backtest</option>
          </select>
          <select
            value={minAgree}
            onChange={(e) => setMinAgree(Number(e.target.value))}
            data-testid="medge-strictness-select"
            className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-xs"
            title="How many rules must agree to fire the meta-signal"
          >
            <option value={1}>≥1 rule (loose)</option>
            <option value={2}>≥2 rules</option>
            <option value={3}>≥3 rules (strict)</option>
            <option value={4}>≥4 rules (very strict)</option>
          </select>
        </div>
      </div>

      {/* TODAY's signal */}
      {ts ? (
        <div
          data-testid="medge-today-signal"
          className={`rounded-lg border-2 p-5 text-center ${
            ts.direction === "UP"
              ? "border-green-600 bg-gradient-to-br from-green-950/50 to-emerald-950/30"
              : "border-red-600 bg-gradient-to-br from-red-950/50 to-rose-950/30"
          }`}
        >
          <div className="flex items-center justify-center gap-2 text-[11px] uppercase tracking-wider text-amber-300 font-bold">
            <Sparkles className="w-3.5 h-3.5" />
            SIGNAL FIRED FOR NEXT TRADING DAY
          </div>
          <div className="mt-3 flex items-center justify-center gap-3">
            {ts.direction === "UP"
              ? <TrendingUp className="w-14 h-14 text-green-400" />
              : <TrendingDown className="w-14 h-14 text-red-400" />}
            <div className={`text-5xl font-extrabold font-mono ${ts.direction === "UP" ? "text-green-300" : "text-red-300"}`}
                 data-testid="medge-today-direction">
              {ts.direction}
            </div>
          </div>
          <div className="text-2xl font-bold font-mono text-white mt-2">{ts.confidence_pct}% conviction</div>
          <div className="text-[11px] text-gray-400 mt-1">{ts.rule_count} rules agree:</div>
          <div className="mt-2 flex items-center justify-center gap-1 flex-wrap">
            {ts.rules_fired.map((r) => (
              <span key={r} className="px-2 py-0.5 rounded bg-gray-800/80 text-gray-200 text-[10px] font-mono border border-gray-700">
                {r}
              </span>
            ))}
          </div>
        </div>
      ) : (
        <div
          data-testid="medge-no-signal"
          className="rounded-lg border-2 border-gray-700 bg-gray-900/40 p-5 text-center"
        >
          <MinusCircle className="w-12 h-12 text-gray-500 mx-auto" />
          <div className="text-lg font-bold text-gray-300 mt-2">NO HIGH-CONFIDENCE SIGNAL TODAY</div>
          <div className="text-xs text-gray-500 mt-1">
            Fewer than {minAgree} rules agree on direction — staying flat is the disciplined play.
          </div>
          {data.today_rules_evaluated?.length > 0 && (
            <div className="mt-3 text-[11px] text-gray-400">
              Rules currently firing ({data.today_rules_evaluated.length}):
              <div className="mt-1 flex items-center justify-center gap-1 flex-wrap">
                {data.today_rules_evaluated.map((r, i) => (
                  <span key={i} className={`px-2 py-0.5 rounded text-[10px] font-mono border ${
                    r.direction === "UP"
                      ? "bg-green-950/40 text-green-300 border-green-800/60"
                      : "bg-red-950/40 text-red-300 border-red-800/60"
                  }`}>
                    {r.rule} → {r.direction}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Verdict + headline backtest stats */}
      <div className={`rounded-lg border p-3 ${verdictColor}`} data-testid="medge-verdict">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4" />
            <span className="text-sm font-bold uppercase tracking-wide">{data.verdict}</span>
          </div>
          <div className="text-[10px] font-mono opacity-80">
            {bt.years_backtested || data.years_backtested}y backtest · ≥{data.min_rules_to_agree} rule consensus
          </div>
        </div>
      </div>

      {/* Backtest summary grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="medge-stats-grid">
        <Stat
          label="Accuracy When Fired"
          value={acc != null ? `${(acc * 100).toFixed(2)}%` : "—"}
          sub={bt.ci_95_low != null ? `95% CI [${(bt.ci_95_low * 100).toFixed(1)}–${(bt.ci_95_high * 100).toFixed(1)}%]` : null}
          color={acc >= 0.75 ? "text-emerald-300" : acc >= 0.65 ? "text-green-300" : acc >= 0.58 ? "text-blue-300" : "text-amber-300"}
          highlight
          icon={<Crosshair className="w-3 h-3" />}
        />
        <Stat
          label="Signal Coverage"
          value={`${bt.coverage_pct}%`}
          sub={`${bt.signal_fires} of ${bt.total_trading_days} days`}
          color="text-cyan-300"
        />
        <Stat
          label="UP Signals"
          value={bt.up_signals}
          sub={bt.up_accuracy != null ? `${(bt.up_accuracy * 100).toFixed(1)}% accurate` : null}
          color="text-green-300"
        />
        <Stat
          label="DOWN Signals"
          value={bt.down_signals}
          sub={bt.down_accuracy != null ? `${(bt.down_accuracy * 100).toFixed(1)}% accurate` : null}
          color="text-red-300"
        />
      </div>

      {/* Rule-by-rule breakdown */}
      {bt.rule_breakdown?.length > 0 && (
        <div className="rounded border border-gray-800 bg-[#0a0a0a] p-3 space-y-2" data-testid="medge-rule-breakdown">
          <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold flex items-center gap-1">
            <BarChart3 className="w-3.5 h-3.5" />
            Per-Rule Win Rate ({data.years_backtested}y backtest, ≥10 fires required)
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-wide text-gray-500 border-b border-gray-800">
                  <th className="text-left py-1.5 px-2 font-semibold">Rule</th>
                  <th className="text-right py-1.5 px-2 font-semibold">Fires</th>
                  <th className="text-right py-1.5 px-2 font-semibold">Wins</th>
                  <th className="text-right py-1.5 px-2 font-semibold">Win Rate</th>
                  <th className="text-left py-1.5 px-2 font-semibold w-1/3">Strength</th>
                </tr>
              </thead>
              <tbody>
                {bt.rule_breakdown.map((r) => {
                  const wr = r.win_rate;
                  const wrColor = wr >= 0.7 ? "text-emerald-300" : wr >= 0.6 ? "text-green-300" : wr >= 0.55 ? "text-blue-300" : "text-amber-300";
                  return (
                    <tr key={r.rule} className="border-b border-gray-800/60 hover:bg-gray-800/30" data-testid={`medge-rule-${r.rule.replace(/[^a-z0-9]/gi, "_").toLowerCase()}`}>
                      <td className="py-1.5 px-2 text-gray-200 font-mono text-[11px]">{r.rule}</td>
                      <td className="py-1.5 px-2 text-right font-mono text-gray-400">{r.fires}</td>
                      <td className="py-1.5 px-2 text-right font-mono text-gray-300">{r.wins}</td>
                      <td className={`py-1.5 px-2 text-right font-mono font-bold ${wrColor}`}>{(wr * 100).toFixed(1)}%</td>
                      <td className="py-1.5 px-2">
                        <div className="w-full bg-gray-800 rounded h-1.5 overflow-hidden">
                          <div
                            className={`h-full ${wr >= 0.7 ? "bg-emerald-500" : wr >= 0.6 ? "bg-green-500" : wr >= 0.55 ? "bg-blue-500" : "bg-amber-500"}`}
                            style={{ width: `${Math.min(100, wr * 100)}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent fires bar chart */}
      {bt.history?.length > 0 && (
        <div className="rounded border border-gray-800 bg-[#0a0a0a] p-3 space-y-2" data-testid="medge-history-chart">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
              Last {bt.history.length} Signal Fires
            </div>
            <div className="text-[10px] font-mono text-gray-500">
              {bt.history.filter(h => h.correct).length}/{bt.history.length} correct
            </div>
          </div>
          <ResponsiveContainer width="100%" height={150}>
            <BarChart data={bt.history} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="date" stroke="#9ca3af" fontSize={9} interval={Math.max(0, Math.floor(bt.history.length / 8))} />
              <YAxis stroke="#9ca3af" fontSize={9} domain={[0, 1]} tickFormatter={(v) => `${(v * 100).toFixed(0)}%`} />
              <Tooltip content={<HistTip />} />
              <ReferenceLine y={0.5} stroke="#6b7280" strokeDasharray="3 3" />
              <Bar dataKey="confidence">
                {bt.history.map((h, i) => (
                  <Cell key={i} fill={h.correct ? "#22c55e" : "#ef4444"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="flex items-center gap-3 text-[10px] text-gray-500 font-mono">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-green-500 rounded-sm" /> correct</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-red-500 rounded-sm" /> wrong</span>
            <span>Bar height = signal conviction</span>
          </div>
        </div>
      )}

      {/* Active rules accordion */}
      <details
        className="rounded border border-gray-800 bg-gray-900/40 px-3 py-2 text-xs"
        data-testid="medge-rules-accordion"
        onToggle={(e) => setShowRules(e.target.open)}
      >
        <summary className="cursor-pointer text-gray-300 font-semibold flex items-center gap-1">
          {showRules ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          Active rules in this engine ({data.active_rules?.length || 0})
        </summary>
        <ol className="list-decimal ml-5 mt-2 space-y-1 text-gray-400">
          {data.active_rules?.map((r, i) => <li key={i}>{r}</li>)}
        </ol>
      </details>

      {/* Honesty footer */}
      <div className="rounded border border-amber-900/40 bg-amber-950/20 p-3 text-[11px] text-amber-200/80">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <strong className="text-amber-200">Honest accuracy expectations:</strong> Liquid markets are
            near-random next-day. Achieving 75–90% accuracy on every day is impossible without overfitting.
            This engine deliberately STAYS SILENT most days and only fires when multiple research-backed
            setups agree — trading 12% coverage for &gt;65% accuracy is a far better strategy than chasing
            55% on every day. Increase strictness (≥3 or ≥4 rules) to climb toward 75%+ at the cost of
            even rarer fires.
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, color = "text-white", icon, highlight }) {
  return (
    <div className={`rounded border ${highlight ? "border-amber-700/60 bg-amber-950/20" : "border-gray-800 bg-gray-900/60"} p-3`}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
        {icon}{label}
      </div>
      <div className={`mt-1 font-mono text-lg font-bold ${color}`}>{value}</div>
      {sub && <div className="text-[10px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function HistTip({ active, payload }) {
  if (!active || !payload?.[0]) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-[11px] font-mono">
      <div className="text-gray-400">{p.date}</div>
      <div className="text-white">Predicted: <span className={p.predicted === "UP" ? "text-green-300" : "text-red-300"}>{p.predicted}</span></div>
      <div className="text-white">Actual: <span className={p.actual === "UP" ? "text-green-300" : "text-red-300"}>{p.actual}</span></div>
      <div className={p.correct ? "text-green-400" : "text-red-400"}>{p.correct ? "✓ Correct" : "✗ Wrong"} · {(p.confidence * 100).toFixed(0)}% conviction</div>
      <div className="text-gray-500 mt-1">{p.rule_count} rules</div>
    </div>
  );
}
